import React from 'react';
import { array, arrayOf, oneOf, string } from 'prop-types';
import { Link } from 'react-router-dom';

import { propTypes } from '../../util/types';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { getCategoryUrl } from '../../util/categoryLandingHelpers';

import css from './CategoryBreadcrumbs.module.css';

// Generate search page URL with category filters up to a certain level
const getSearchUrl = (breadcrumbPath, upToIndex) => {
  const params = new URLSearchParams();

  for (let i = 0; i <= upToIndex; i++) {
    const category = breadcrumbPath[i];
    const level = i + 1; // Level is 1-indexed
    params.set(`pub_categoryLevel${level}`, category.id);
  }

  return `/s?${params.toString()}`;
};

/**
 * CategoryBreadcrumbs component
 * Renders a breadcrumb navigation showing the full category hierarchy path
 *
 * Example: Home > Wind & Reed > Flute > Concert / Boehm Flute
 *
 * @param {string} linkMode - 'landing' for category landing pages, 'search' for search page with filters
 */
const CategoryBreadcrumbs = props => {
  const { breadcrumbPath, routeConfiguration, linkMode } = props;
  const intl = useIntl();

  if (!breadcrumbPath || breadcrumbPath.length === 0) {
    return null;
  }

  return (
    <nav
      className={css.root}
      aria-label={intl.formatMessage({ id: 'CategoryBreadcrumbs.label' })}
    >
      <ol className={css.list} itemScope itemType="https://schema.org/BreadcrumbList">
        {/* Home (not a link) */}
        <li
          className={css.item}
          itemProp="itemListElement"
          itemScope
          itemType="https://schema.org/ListItem"
        >
          <span className={css.home} itemProp="name">
            <FormattedMessage id="CategoryBreadcrumbs.home" />
          </span>
          <meta itemProp="position" content="1" />
          <span className={css.separator} aria-hidden="true">
            /
          </span>
        </li>

        {/* Category path */}
        {breadcrumbPath.map((category, index) => {
          const isLast = index === breadcrumbPath.length - 1;
          const position = index + 2; // +2 because Home is position 1

          // Use search URL or landing page URL based on linkMode
          const categoryUrl = linkMode === 'search'
            ? getSearchUrl(breadcrumbPath, index)
            : getCategoryUrl(category.id);

          return (
            <li
              key={category.id}
              className={css.item}
              itemProp="itemListElement"
              itemScope
              itemType="https://schema.org/ListItem"
            >
              {isLast ? (
                <span className={css.current} itemProp="name" aria-current="page">
                  {category.name}
                </span>
              ) : (
                <>
                  <Link to={categoryUrl} className={css.link} itemProp="item">
                    <span itemProp="name">{category.name}</span>
                  </Link>
                  <span className={css.separator} aria-hidden="true">
                    /
                  </span>
                </>
              )}
              <meta itemProp="position" content={String(position)} />
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

CategoryBreadcrumbs.defaultProps = {
  breadcrumbPath: [],
  routeConfiguration: [],
  linkMode: 'landing', // 'landing' for category pages, 'search' for search page
};

CategoryBreadcrumbs.propTypes = {
  breadcrumbPath: array,
  routeConfiguration: arrayOf(propTypes.route),
  linkMode: oneOf(['landing', 'search']),
};

export default CategoryBreadcrumbs;
